import { motion } from 'framer-motion';
import { Camera, ChevronRight, ListChecks, Zap } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useStore } from '../store/useStore';
import { publicUrl } from '../native/native';

/** Onboarding Screen 1 — the value hook. */
export function HookScreen() {
  const setScreen = useStore((s) => s.setScreen);

  /** Returning-user front door (V2 §, register #20, Roxanne): a new device
   *  must never force an existing user through the trial funnel. Same flow
   *  as Settings → Sign in with Google; on success their week syncs down
   *  and the app lands on the matrix by itself. */
  const openSettings = useStore((s) => s.openSettings);
  const lastAccount = useStore((s) => s.lastAccount);
  const signIn = () => {
    setScreen('matrix');
    // A device that remembers its last account goes straight back to it
    // (13 Oct 2026) — the same one-tap path as the sync chip.
    if (lastAccount) {
      void import('../sync/firebaseSync').then((m) => m.signInAsLastAccount());
      return;
    }
    // iOS: Apple requires the privacy option wherever Google is offered
    // (4.8), so the front door opens the Settings sign-in card, which has
    // both buttons. Android/web keep the one-tap Google path.
    void import('../native/native').then((n) => {
      if (n.isIOS()) openSettings();
      else void import('../sync/firebaseSync').then((m) => m.signInWithGoogle());
    });
  };

  return (
    <div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-between px-6 py-10 lg:max-w-2xl lg:justify-center lg:gap-10">
      <div className="space-y-8">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 18 }}
          className="mx-auto flex items-center justify-center rounded-3xl shadow-xl"
        >
          <Logo size={84} />
        </motion.div>

        <div className="space-y-3 text-center">
          <span className="inline-block rounded-full border border-brand-200 dark:border-brand-500/20 bg-brand-500/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-brand-600 dark:text-brand-400">
            Your Weekly Operating System
          </span>
          <h2 className="text-3xl font-black leading-tight tracking-tight text-slate-900 dark:text-white">
            Clear the mental clutter in{' '}
            <span className="text-brand-600 dark:text-brand-400">30 seconds</span>.
          </h2>
          <p className="mx-auto max-w-xs text-sm leading-relaxed text-slate-500 dark:text-slate-400">
            Stop managing messy to-do lists. Let AI organize your week by impact
            and immediacy so you can focus on execution.
          </p>
        </div>

        <div className="space-y-3">
          {[
            { icon: Zap, text: 'Dump your thoughts — typed, dictated, or scanned from paper' },
            { icon: Camera, text: 'Snap sticky notes or whiteboards; AI digitizes them into tasks' },
            { icon: ListChecks, text: 'Weekly rollover, procrastination audits, and mastery analytics' },
          ].map(({ icon: Icon, text }, i) => (
            <motion.div
              key={text}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15 + i * 0.12 }}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3.5"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-brand-200 dark:border-brand-500/20 bg-brand-500/10 text-brand-600 dark:text-brand-400">
                <Icon size={17} />
              </span>
              <p className="text-xs font-medium leading-relaxed text-slate-600 dark:text-slate-300">{text}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="space-y-3 pt-8">
        <button
          onClick={() => setScreen('dump')}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 py-4 text-center text-sm font-bold text-white shadow-lg shadow-brand-600/30 transition-all hover:bg-brand-500 active:scale-[0.98]"
        >
          <span>Get Started — Free</span>
          <ChevronRight size={16} />
        </button>
        {/* Returning-user front door (register #20): visible on the FIRST
            screen — no forcing existing users through the trial funnel. */}
        <button
          onClick={signIn}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-3 text-center text-xs font-bold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          {lastAccount ? `Continue as ${lastAccount.email}` : 'Already have an account? Sign in'}
        </button>
        {/* 18 Oct 2026: the old line said ZoneIn had no password sign-in —
            untrue since email + password sign-in (13 Oct). */}
        <p className="text-center text-[11px] leading-relaxed text-slate-500">
          Sign in once and your week syncs to every device you use — or skip
          it: no account is needed.
        </p>
        <p className="text-center text-xs font-semibold text-slate-600 dark:text-slate-400">
          🛡 No trackers · No ads · No training on your data · No account needed
        </p>
        <p className="text-center text-[11px] text-slate-500">
          Your tasks stay on your device.{' '}
          <a
            href={publicUrl('/privacy.html')}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-brand-600 dark:text-brand-400 underline-offset-2 hover:underline"
          >
            How we protect your data →
          </a>
        </p>
        <p className="text-center text-[11px] text-slate-500">
          New here?{' '}
          <a
            href={publicUrl('/guide.html')}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-brand-600 dark:text-brand-400 underline-offset-2 hover:underline"
          >
            Read the 2-minute guide →
          </a>
        </p>
      </div>
    </div>
  );
}
